import React from 'react';

import styled from '@emotion/styled';

const tierColorMap: Record<string, string> = {
  'at risk': '#ff0500',
  platinum: '#5b91f5',
  gold: '#ffc400',
  silver: '#6b7280',
};

const resolveTierColor = (tierName: string, fallbackColor?: string): string => {
  const normalizedTier = tierName.trim().toLowerCase();
  return tierColorMap[normalizedTier] ?? fallbackColor ?? '#757A85';
};

type TooltipPayloadEntry = {
  name?: string;
  label?: string;
  value?: number;
  color?: string;
  payload?: {
    route?: string;
    tierName?: string;
    count?: number;
  };
};

type RouteAnalysisTooltipProps = {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
};

const RouteAnalysisTooltip = ({
  active,
  payload,
}: RouteAnalysisTooltipProps) => {
  if (active && payload && payload.length) {
    const firstEntry = payload[0];
    const tierName: string =
      firstEntry?.payload?.route ??
      firstEntry?.payload?.tierName ??
      firstEntry?.label ??
      firstEntry?.name ??
      'Unknown route';
    const tierScore = firstEntry?.value ?? firstEntry?.payload?.count ?? 0;
    const tierColor = resolveTierColor(tierName, firstEntry?.color);

    return (
      <TooltipShell>
        <ColorDot color={tierColor} />
        <TooltipContainer>
          <TierLabel>{tierName}</TierLabel>
          <ScoreValue>{tierScore}</ScoreValue>
        </TooltipContainer>
      </TooltipShell>
    );
  }
  return null;
};

export default RouteAnalysisTooltip;

const TooltipShell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ColorDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const TooltipContainer = styled.div`
  background: #ffffff;
  border-radius: 6px;
  padding: 12px;
  box-shadow: 0px 4px 12px rgba(17, 24, 39, 0.14);
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 88px;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const TierLabel = styled.span`
  font-size: 14px;
  line-height: 1;
  color: #7a8091;
  font-weight: 400;
  letter-spacing: 0;
`;

const ScoreValue = styled.span`
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
  color: #111111;
  letter-spacing: -0.01em;
`;
